/**
 * Audit Log Types
 * Types and interfaces for the audit log system
 */

export enum AuditAction {
  // Authentication
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_FAILED = 'LOGIN_FAILED',
  PASSWORD_RESET = 'PASSWORD_RESET',
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',
  
  // CRUD operations
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  RESTORE = 'RESTORE',
  
  // System
  SYSTEM_START = 'SYSTEM_START',
  SYSTEM_SHUTDOWN = 'SYSTEM_SHUTDOWN',
  CONFIG_CHANGED = 'CONFIG_CHANGED',
  
  // Security
  UNAUTHORIZED_ACCESS = 'UNAUTHORIZED_ACCESS',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  ACCOUNT_UNLOCKED = 'ACCOUNT_UNLOCKED',
  
  // Payment
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  SUBSCRIPTION_CREATED = 'SUBSCRIPTION_CREATED',
  SUBSCRIPTION_UPDATED = 'SUBSCRIPTION_UPDATED',
  SUBSCRIPTION_CANCELLED = 'SUBSCRIPTION_CANCELLED',
  REFUND_ISSUED = 'REFUND_ISSUED',
  
  // File operations
  FILE_UPLOADED = 'FILE_UPLOADED',
  FILE_DOWNLOADED = 'FILE_DOWNLOADED',
  FILE_DELETED = 'FILE_DELETED',
  
  // Data export
  EXPORT_STARTED = 'EXPORT_STARTED',
  EXPORT_COMPLETED = 'EXPORT_COMPLETED',
  EXPORT_FAILED = 'EXPORT_FAILED',
}

export enum AuditSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL',
}

export enum AuditCategory {
  USER_ACTION = 'USER_ACTION',
  SYSTEM = 'SYSTEM',
  SECURITY = 'SECURITY',
  PERFORMANCE = 'PERFORMANCE',
  PAYMENT = 'PAYMENT',
  DATA = 'DATA',
}

export interface AuditLogUser {
  id: number;
  email: string;
  nom: string;
  prenom: string;
}

export interface AuditLog {
  id: string;
  timestamp: Date;
  userId: number | null;
  user?: AuditLogUser;
  ipAddress: string | null;
  userAgent: string | null;
  action: AuditAction;
  resource: string;
  resourceId?: string | null;
  method: string | null;
  endpoint: string | null;
  statusCode: number | null;
  oldValues?: Record<string, any> | null;
  newValues?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
  severity: AuditSeverity;
  category: AuditCategory;
  duration?: number | null;
  errorMessage?: string | null;
  errorStack?: string | null;
}

export interface AuditLogFilters {
  userId?: number;
  action?: AuditAction | AuditAction[];
  resource?: string | string[];
  severity?: AuditSeverity | AuditSeverity[];
  category?: AuditCategory | AuditCategory[];
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AuditLogStats {
  total: number;
  byAction: Record<string, number>;
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
  byResource: Record<string, number>;
  topUsers: Array<{
    userId: number;
    userName: string;
    count: number;
  }>;
  errorRate: number;
  avgDuration: number;
  recentErrors: AuditLog[];
  timeline: Array<{
    date: string;
    count: number;
    bySeverity: Record<string, number>;
  }>;
}

export interface PaginatedAuditLogs {
  data: AuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuditLogExportOptions {
  filters: AuditLogFilters;
  format: 'csv' | 'json' | 'xlsx';
  includeMetadata?: boolean;
}
